// Utils used for change of cover
import { OperationType, VeteranCardTypeOptions, EligibilityTypes } from '@/enums';
import { formatFormDate } from '@general/js/utils';

export const mapVeteranCardConditions = (veteranCardConditions) => {
  return veteranCardConditions.map((condition) => {
    const mappedCondition = {
      op: condition.op,
      id: condition.id,
      code: condition.returnValue,
      startDate: condition.startDate,
    };

    if (condition.endDate) {
      mappedCondition.endDate = condition.endDate;
    }
    return mappedCondition;
  });
};

export const convertVeteranCardTypeToCode = (veteranCardType) => {
  if (veteranCardType) {
    if (veteranCardType.toLowerCase() === VeteranCardTypeOptions.VETERAN_GOLD_CARD) {
      return VeteranCardTypeOptions.VETERAN_GOLD_CODE;
    }
    if (veteranCardType.toLowerCase() === VeteranCardTypeOptions.VETERAN_WHITE_CARD) {
      return VeteranCardTypeOptions.VETERAN_WHITE_CODE;
    }
  }

  return null;
};

/**
 * This function is used to craft the Concession Cards `cardList` payload used for Change Cover submission.
 * @param {boolean} initialHaveConcessionCard Did the initial partner/current partner initially have a concession card?
 * @param {Array} initialConcessionCardList If yes, the initial concession card list
 * @param {boolean} newHaveConcessionCard Does the current partner being added have a concession card?
 * @param {Array} newConcessionCardList If yes, current concession card list
 * @param {boolean} noLongerEligibleForConcession true if the member is no longer eligible for concession cards. false by default.
 * @param {boolean} showConcessionCardDetailsForm boolean to determine if concession card details form(card no, type, expiry, etc) is displayed(based on concession card business rules)
 * @returns Mapped array for concession cards with appropriate operations as required
 */
export const mapConcessionCardPayload = (
  initialHaveConcessionCard,
  initialConcessionCardList,
  newHaveConcessionCard,
  newConcessionCardList,
  noLongerEligibleForConcession = false,
  showConcessionCardDetailsForm,
) => {
  const initialCardList = initialConcessionCardList.map((card) => {
    return {
      op: card.op,
      id: card.id,
      cardType: card.name,
      cardNumber: card.number,
      expiryDate: formatFormDate(card.expire), // optional for some cards
      startDate: formatFormDate(card.startDate), // YYYY-MM-DD
    };
  });

  const newCardList = newConcessionCardList.map((card) => {
    return {
      id: card.id,
      op: card.op,
      cardType: card.name,
      cardNumber: card.number,
      expiryDate: formatFormDate(card.expire), // optional for some cards
      startDate: formatFormDate(card.startDate), // YYYY-MM-DD
    };
  });

  // if had initial concession cards, and no longer eligible for concession, remove all cards in list
  if (noLongerEligibleForConcession) {
    return initialCardList.map((card) => {
      return {
        ...card,
        op: OperationType.REMOVE,
      };
    });
  }

  // if previously had concession card(CC) and currently have it too, but new state doesn't require card details(showConcessionCardDetailsForm=false), only Yes/No, then remove all old cards
  if (initialHaveConcessionCard && newHaveConcessionCard && !showConcessionCardDetailsForm) {
    return initialCardList.map((card) => {
      return {
        ...card,
        op: OperationType.REMOVE,
      };
    });
  }

  // if have a new and old concession card list, combine them and add all 'remove' with cards in new list
  if (initialHaveConcessionCard && newHaveConcessionCard) {
    const removedConcessionCards = initialCardList.filter((card) => {
      return card.op === OperationType.REMOVE;
    });
    return newCardList.concat(removedConcessionCards);
  }

  // if initially had concession card but now does not, end date all existing cards
  if (initialHaveConcessionCard) {
    return initialCardList.map((card) => {
      return {
        ...card,
        op: OperationType.REMOVE,
      };
    });
  }

  // return the new card list if there was no initial card list (all operation should be ADD)
  if (newHaveConcessionCard) {
    return newCardList;
  }
  return [];
};

/**
 * Convert eligibility name from API response to eligibility type name
 * @param {string} eligibilityName String value of eligibility returned in response
 * @returns {string} Eligibility type name for the given name in the EligibilityType category. Eg, ADF, Ex-Serving, etc or '' if not found
 */
export const mapEligibilityNames = (eligibilityName) => {
  let currentEligibility = '';
  if (EligibilityTypes.SERVING.includes(eligibilityName)) {
    currentEligibility = EligibilityTypes.SERVING[0];
  } else if (EligibilityTypes.EX_SERVING.includes(eligibilityName)) {
    currentEligibility = EligibilityTypes.EX_SERVING[0];
  } else if (EligibilityTypes.WORK_CONNECTION.includes(eligibilityName)) {
    currentEligibility = EligibilityTypes.WORK_CONNECTION[0];
  } else if (EligibilityTypes.RELATED.includes(eligibilityName)) {
    currentEligibility = EligibilityTypes.RELATED[0];
  }
  return currentEligibility;
};

/**
 * Convert pregnancy flag string to boolean
 * @param {string|boolean} pregnancyFlag String value of excludePregnancy returned in response
 * @returns {boolean} true if "true", false if "false", otherwise null 
 */
export const convertPregnancyFlagStringToBool = (pregnancyFlag) => {
  if (pregnancyFlag === 'true' || pregnancyFlag === true) {
    return true;
  } 
  if (pregnancyFlag === 'false' || pregnancyFlag === false) {
    return false;
  }
  return null;
};
